/**
 * Error reporter — fire-and-forget error tracking via POST /api/errors/report.
 * Deduplicates by fingerprint (errorType:errorMessage) within 60s window.
 */

import { useAuthStore } from "@/stores/authStore";
import {
  ensureTelemetrySession,
  getActiveWorkflowAttemptId,
  getClientVersion,
} from "@/utils/telemetryContext";

const DEDUP_WINDOW_MS = 60_000;
const recentErrors = new Map<string, number>();
const EXTENSION_URL_RE = /\b(?:chrome|moz|safari-web)-extension:\/\//i;

function getFingerprint(errorType?: string, errorMessage?: string): string {
  return `${errorType ?? ""}:${errorMessage ?? ""}`;
}

function getErrorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("status" in error)) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

function isExpectedApiRequestError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== "ApiRequestError") return false;
  const status = getErrorStatus(error);
  return status !== null && status >= 400 && status < 500;
}

function isOpaqueScriptError(message: string, source?: string, line?: number, column?: number): boolean {
  return message === "Script error." && !source && (line ?? 0) === 0 && (column ?? 0) === 0;
}

function isBrowserExtensionError(params: { message?: string; source?: string; stack?: string }): boolean {
  const text = `${params.source ?? ""}\n${params.stack ?? ""}\n${params.message ?? ""}`;
  return EXTENSION_URL_RE.test(text) || /Failed to connect to MetaMask/i.test(text);
}

/**
 * reportError — sends error to backend error tracking endpoint.
 * Fire-and-forget: never throws, never blocks UI.
 */
export function reportError(params: {
  severity: "fatal" | "error" | "warning" | "info";
  errorType?: string;
  errorMessage: string;
  errorStack?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}): void {
  try {
    const fingerprint = getFingerprint(params.errorType, params.errorMessage);
    const now = Date.now();
    const lastSent = recentErrors.get(fingerprint);
    if (lastSent && now - lastSent < DEDUP_WINDOW_MS) return;
    recentErrors.set(fingerprint, now);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Best-effort auth token attachment
    try {
      const auth = useAuthStore.getState();
      const token = auth.token;
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const sessionId = auth.user?.id ? ensureTelemetrySession(auth.user.id).sessionId : undefined;
      params.tags = {
        ...params.tags,
        ...(sessionId ? { sessionId } : {}),
        ...(getActiveWorkflowAttemptId() ? { workflowAttemptId: getActiveWorkflowAttemptId()! } : {}),
        appVersion: getClientVersion(),
      };
    } catch {
      // auth store may not be initialised yet — skip
    }

    fetch("/api/errors/report", {
      method: "POST",
      headers,
      body: JSON.stringify({
        severity: params.severity,
        errorType: params.errorType,
        errorMessage: params.errorMessage,
        errorStack: params.errorStack,
        url: window.location.href,
        tags: params.tags,
        extra: params.extra,
      }),
    }).catch(() => {});
  } catch {
    // Never throw — error reporting must not break the app
  }
}

/**
 * setupGlobalErrorHandlers — installs window.onerror and unhandledrejection listener.
 * Call once on app startup.
 */
export function setupGlobalErrorHandlers(): void {
  window.onerror = (message, source, lineno, colno, error) => {
    const errorMessage = String(message);
    if (
      isOpaqueScriptError(errorMessage, source, lineno, colno)
      || isBrowserExtensionError({ message: errorMessage, source, stack: error?.stack })
    ) {
      return;
    }

    reportError({
      severity: "error",
      errorType: error?.name,
      errorMessage,
      errorStack: error?.stack,
      tags: {
        source: source || "",
        line: String(lineno ?? ""),
        col: String(colno ?? ""),
      },
    });
  };

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (isExpectedApiRequestError(reason)) return;

    if (reason instanceof Error) {
      if (isBrowserExtensionError({ message: reason.message, stack: reason.stack })) return;

      reportError({
        severity: "error",
        errorType: reason.name,
        errorMessage: reason.message,
        errorStack: reason.stack,
        tags: { source: "unhandledrejection" },
      });
    } else {
      reportError({
        severity: "error",
        errorType: "UnhandledRejection",
        errorMessage: String(reason),
        tags: { source: "unhandledrejection" },
      });
    }
  });
}
